import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Star, MapPin, Users, BedDouble, Check, ChevronLeft, Crown, Camera, ArrowLeft } from "lucide-react";
import { useState } from "react";
import { formatNaira, hasGuestRating, type RoomType } from "@/data/venues";
import { VenueService } from "@/services/venue.service";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/room-detail/$venueId/$roomId")({
  loader: async ({ params }) => {
    const venue = await VenueService.getVenueById(params.venueId);
    if (!venue) throw notFound();
    const room = venue.roomTypes?.find((r: RoomType) => r.id === params.roomId);
    if (!room) throw notFound();
    return { venue, room };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { venue, room } = loaderData;
    return {
      meta: [
        { title: `${room.name} — ${venue.name}` },
        { name: "description", content: room.description },
      ],
    };
  },
  component: RoomDetailPage,
  notFoundComponent: () => (
    <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 text-center">
      <h2 className="font-display text-2xl font-bold">Room not found</h2>
      <p className="mt-2 text-muted-foreground">This room type doesn't exist or has been removed.</p>
      <Link to="/" className="mt-6 text-primary underline">Back to homepage</Link>
    </div>
  ),
});

function RoomDetailPage() {
  const { venue, room } = Route.useLoaderData();
  const [activePhoto, setActivePhoto] = useState(0);

  const photos = room.images && room.images.length > 0 ? room.images : [venue.image];

  return (
    <>
      {/* Sticky top bar */}
      <div className="sticky top-0 z-30 border-b border-border/50 bg-background/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-3">
          <Link
            to="/venues/$venueId"
            params={{ venueId: venue.id }}
            className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-4" />
            Back to {venue.name}
          </Link>
        </div>
      </div>

      {/* Hero photo gallery */}
      <section className="mx-auto max-w-5xl px-4 pt-6">
        {/* Main photo */}
        <div className="relative aspect-[16/9] w-full overflow-hidden rounded-2xl bg-muted">
          <img
            src={photos[activePhoto]}
            alt={`${room.name} — Photo ${activePhoto + 1}`}
            className="size-full object-cover transition-all duration-500"
          />
          <div className="absolute bottom-4 right-4 rounded-lg bg-black/50 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm">
            {activePhoto + 1} / {photos.length}
          </div>
        </div>

        {/* Thumbnail strip */}
        {photos.length > 1 && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {photos.map((img: string, i: number) => (
              <button
                key={i}
                onClick={() => setActivePhoto(i)}
                className={`relative shrink-0 size-20 rounded-xl overflow-hidden border-2 transition-all ${
                  i === activePhoto ? "border-primary shadow-sm" : "border-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img src={img} alt={`Thumbnail ${i + 1}`} className="size-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Room details */}
      <section className="mx-auto max-w-5xl px-4 py-6">
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left: Details */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <div className="flex items-center gap-3">
                <Crown className="size-6 text-primary" />
                <h1 className="font-display text-3xl font-bold">{room.name}</h1>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">at {venue.name} • {venue.location}</p>
            </div>

            <p className="text-foreground/80 leading-relaxed text-base">{room.description}</p>

            {/* Quick info chips */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-2.5">
                <Users className="size-4 text-primary" />
                <span className="text-sm font-medium">Up to {room.maxGuests} guests</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-2.5">
                <Camera className="size-4 text-primary" />
                <span className="text-sm font-medium">{photos.length} photo{photos.length !== 1 ? "s" : ""}</span>
              </div>
              <div className="flex items-center gap-2 rounded-xl bg-accent/50 px-4 py-2.5">
                <BedDouble className="size-4 text-primary" />
                <span className="text-sm font-medium">{venue.category === "hotel" ? "Per night" : "Per event"}</span>
              </div>
            </div>

            {/* Room amenities (room-specific), fallback to venue amenities */}
            {((room.amenities && room.amenities.length > 0) || venue.amenities.length > 0) && (
              <div>
                <h3 className="font-display text-lg font-semibold mb-3">What's included</h3>
                <div className="grid grid-cols-2 gap-2">
                  {(room.amenities && room.amenities.length > 0 ? room.amenities : venue.amenities).map((a: string) => (
                    <div key={a} className="flex items-center gap-2 rounded-lg bg-accent/30 px-3 py-2 text-sm">
                      <Check className="size-3.5 text-primary shrink-0" /> {a}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Other room types */}
            {venue.roomTypes && venue.roomTypes.length > 1 && (
              <div>
                <h3 className="font-display text-lg font-semibold mb-3">Other room types</h3>
                <div className="grid gap-3 sm:grid-cols-2">
                  {venue.roomTypes
                    .filter((rt: RoomType) => rt.id !== room.id)
                    .map((rt: RoomType) => (
                      <Link
                        key={rt.id}
                        to="/room-detail/$venueId/$roomId"
                        params={{ venueId: venue.id, roomId: rt.id }}
                        className="flex items-center gap-3 rounded-xl border border-border/60 bg-card p-3 hover:bg-accent/20 hover:border-primary/30 transition-all"
                      >
                        {rt.images && rt.images.length > 0 ? (
                          <img src={rt.images[0]} alt={rt.name} className="size-14 rounded-lg object-cover shrink-0" />
                        ) : (
                          <div className="size-14 rounded-lg bg-muted flex items-center justify-center shrink-0">
                            <BedDouble className="size-5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold truncate">{rt.name}</p>
                          <p className="text-xs text-muted-foreground">{formatNaira(rt.price)} / {venue.category === "hotel" ? "night" : "event"}</p>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Booking card (sticky) */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 rounded-2xl border border-border/60 bg-card p-6 shadow-elegant space-y-4">
              <div>
                <span className="font-display text-3xl font-bold">{formatNaira(room.price)}</span>
                <span className="text-sm text-muted-foreground"> / {venue.category === "hotel" ? "night" : "event"}</span>
              </div>

              <div className="rounded-xl bg-accent/30 p-3 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Star className="size-4 text-primary fill-primary" />
                  <span className="font-medium text-foreground">{hasGuestRating(venue) ? venue.rating.toFixed(1) : "New"}</span>
                  <span>{venue.reviews > 0 ? `${venue.reviews} review${venue.reviews !== 1 ? "s" : ""}` : "No reviews yet"}</span>
                </div>
              </div>

              <Link
                to="/booking/$venueId"
                params={{ venueId: venue.id }}
                className="block w-full"
              >
                <Button className="w-full" size="lg">
                  Book {room.name}
                </Button>
              </Link>

              <p className="text-center text-xs text-muted-foreground">You won't be charged yet</p>

              <div className="border-t border-border pt-3 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="size-3.5" />
                  <span>{venue.location}, Oyo State</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

